import { useEffect, useState, useCallback } from "react";
import api, { BACKEND_URL } from "../lib/api";
import { PageHeader, Card, money, StatusBadge, DateRangeFilter, rangeParams } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, Mail, Globe, Pencil, Trash2, ArrowRight, MousePointerClick, DollarSign, FileText } from "lucide-react";

const SourceForm = ({ initial, onSave }) => {
  const [form, setForm] = useState(initial || { name: "", type: "email", platform: "", domain: "", status: "active", notes: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs">Source Name</Label>
        <Input data-testid="source-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Type</Label>
          <Select value={form.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger data-testid="source-type-select" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="email">Email Platform</SelectItem><SelectItem value="website">Website</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{form.type === "email" ? "Platform (Klaviyo, Mailchimp…)" : "Domain"}</Label>
          <Input value={form.type === "email" ? form.platform : form.domain} onChange={(e) => set(form.type === "email" ? "platform" : "domain", e.target.value)} className="mt-1" />
        </div>
      </div>
      <Button data-testid="source-save-button" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={!form.name} onClick={() => onSave(form)}>
        {initial ? "Update Source" : "Add Source"}
      </Button>
    </div>
  );
};

export default function TrafficSources() {
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rangeSel, setRangeSel] = useState({ range: "this_month" });

  const load = useCallback(() => {
    Promise.all([api.get("/traffic-sources"), api.get(`/stats/entities?${rangeParams(rangeSel)}`)]).then(([s, st]) => { setSources(s.data); setStats(st.data); });
  }, [rangeSel]);
  useEffect(load, [load]);

  const save = async (form) => {
    if (editing) await api.put(`/traffic-sources/${editing.id}`, form);
    else await api.post("/traffic-sources", form);
    toast.success("Saved"); setOpen(false); setEditing(null); load();
  };
  const del = async (id) => { await api.delete(`/traffic-sources/${id}`); toast.success("Deleted"); load(); };

  const snippet = `<script>
(function(){
  var API='${BACKEND_URL}';
  var MAP={};
  var KEYS=['email_id','sub1','sub2','utm_source','utm_medium','utm_campaign'];
  var p=new URLSearchParams(location.search);
  KEYS.forEach(function(k){var v=p.get(k)||(k==='email_id'?p.get('email'):null);
    if(v)document.cookie='omni_'+k+'='+encodeURIComponent(v)+';path=/;max-age=2592000';});
  function c(k){var m=document.cookie.match(new RegExp('omni_'+k+'=([^;]+)'));
    return m?decodeURIComponent(m[1]):null;}
  function tb(id){return (MAP[id]||API)+'/api/t/'+id;}
  function bs(a){
    var ob=a.getAttribute('data-omni-base');
    if(ob===null){ob=(a.getAttribute('href')&&a.href.indexOf('/api/t/')>=0)?a.href:'';a.setAttribute('data-omni-base',ob);}
    return ob||tb(a.getAttribute('data-omni-campaign'));}
  function decorate(a,base){
    try{var u=new URL(base,location.href);
      KEYS.forEach(function(k){var v=c(k);if(v&&!u.searchParams.get(k))u.searchParams.set(k,v);});
      if(!u.searchParams.get('utm_source'))u.searchParams.set('utm_source',location.hostname);
      a.href=u.toString();}catch(e){}}
  function apply(){
    document.querySelectorAll('a[data-omni-campaign]').forEach(function(a){decorate(a,bs(a));});
    document.querySelectorAll('a[href*="/api/t/"]').forEach(function(a){
      if(!a.getAttribute('data-omni-campaign'))decorate(a,a.href);});}
  fetch(API+'/api/track-map').then(function(r){return r.json()}).then(function(m){MAP=m||{};apply();}).catch(function(){});
  apply();
  document.addEventListener('click',function(e){
    var a=e.target&&e.target.closest&&e.target.closest('a[href*="/api/t/"],a[data-omni-campaign]');
    if(!a)return;
    if(a.getAttribute('data-omni-campaign'))decorate(a,bs(a));
    else decorate(a,a.href);
  },true);
})();
</script>`;

  const loanSnippet = `<script>
(function(){
  var API='${BACKEND_URL}/api/events/lead';
  function c(k){var m=document.cookie.match(new RegExp('omni_'+k+'=([^;]+)'));
    return m?decodeURIComponent(m[1]):'';}
  document.querySelectorAll('form[data-omni-loan-form]').forEach(function(f){
    f.addEventListener('submit',function(){
      function v(n){var el=f.querySelector('[name="'+n+'"]');return el?el.value:'';}
      var em=(v('email')||'').trim().toLowerCase();
      if(em&&!c('email_id'))document.cookie='omni_email_id='+encodeURIComponent(em)+';path=/;max-age=2592000';
      fetch(API,{method:'POST',headers:{'Content-Type':'application/json'},keepalive:true,
        body:JSON.stringify({first_name:v('first_name'),last_name:v('last_name'),
        email:em,phone:v('phone'),zip:v('zip'),state:v('state'),
        website:location.hostname,email_id:c('email_id')||em,sub1:c('sub1'),
        utm_campaign:c('utm_campaign')})});
    });
  });
})();
</script>`;

  const copyLoanSnippet = () => { navigator.clipboard.writeText(loanSnippet).then(() => toast.success("Loan form snippet copied")).catch(() => toast.error("Copy failed — select and copy manually")); };

  const copySnippet = () => { navigator.clipboard.writeText(snippet).then(() => toast.success("Tracking snippet copied")).catch(() => toast.error("Copy failed — select and copy manually")); };

  return (
    <div data-testid="traffic-sources-page">
      <PageHeader
        title="Traffic Sources"
        subtitle="Email platform and website funnels feeding your campaigns"
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-source-button" className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1" /> Add Source</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Source" : "Add Traffic Source"}</DialogTitle></DialogHeader>
              <SourceForm key={editing?.id || "new"} initial={editing} onSave={save} />
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="p-5 mb-6 fade-up">
        <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Customer Journey Funnel</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { icon: Mail, label: "Email Sent", sub: "Agent sends to customer" },
            { icon: Globe, label: "Website Visit", sub: "Identity saved via cookies" },
            { icon: FileText, label: "Loan Form", sub: "Lead captured (optional)" },
            { icon: MousePointerClick, label: "Banner / CTA Click", sub: "/api/t/{campaign} logs click" },
            { icon: DollarSign, label: "Conversion", sub: "Network fires S2S postback" },
          ].map((s, i, arr) => (
            <div key={s.label} className="flex items-center gap-2">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3">
                <div className="h-8 w-8 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center"><s.icon className="h-4 w-4" /></div>
                <div>
                  <div className="text-xs font-semibold text-slate-800">{i + 1}. {s.label}</div>
                  <div className="text-[10px] text-slate-400">{s.sub}</div>
                </div>
              </div>
              {i < arr.length - 1 && <ArrowRight className="h-4 w-4 text-slate-300" />}
            </div>
          ))}
        </div>
      </Card>

      <div className="mb-4 fade-up"><DateRangeFilter value={rangeSel} onChange={setRangeSel} /></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6" data-testid="sources-grid">
        {sources.map((s) => {
          const st = stats[s.id] || { clicks: 0, conversions: 0, revenue: 0 };
          const Icon = s.type === "email" ? Mail : Globe;
          return (
            <Card key={s.id} className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${s.type === "email" ? "bg-amber-50 text-amber-600" : "bg-sky-50 text-sky-600"}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-sm text-slate-900">{s.name}</div>
                    <div className="text-[11px] text-slate-400">{s.type === "email" ? s.platform : s.domain}</div>
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                <div><div className="font-mono text-sm font-semibold">{st.clicks.toLocaleString()}</div><div className="text-[10px] text-slate-400 uppercase">Clicks</div></div>
                <div><div className="font-mono text-sm font-semibold">{st.conversions}</div><div className="text-[10px] text-slate-400 uppercase">Conv.</div></div>
                <div><div className="font-mono text-sm font-semibold text-emerald-600">{money(st.revenue)}</div><div className="text-[10px] text-slate-400 uppercase">Revenue</div></div>
              </div>
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(s); setOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => del(s.id)}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading text-sm font-semibold text-slate-900">Website Tracking Snippet</h3>
            <p className="text-xs text-slate-400 mt-0.5">Paste right before &lt;/body&gt; on every page of both websites. It stores email/UTM identifiers as 1st-party cookies and rewrites any link with data-omni-campaign="CAMPAIGN_ID" into a tracked URL.</p>
          </div>
          <Button data-testid="copy-snippet-button" variant="outline" size="sm" onClick={copySnippet}><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy</Button>
        </div>
        <pre className="bg-slate-900 rounded-lg p-4 text-[11px] text-emerald-400 overflow-x-auto scrollbar-thin"><code data-testid="tracking-snippet">{snippet}</code></pre>
      </Card>

      <Card className="p-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading text-sm font-semibold text-slate-900 flex items-center gap-2"><FileText className="h-4 w-4 text-sky-500" /> Loan Form Capture Snippet</h3>
            <p className="text-xs text-slate-400 mt-0.5">Paste before &lt;/body&gt; on pages with your loan form, and add data-omni-loan-form to the &lt;form&gt; tag. Every submission becomes a Lead here, automatically linked to the customer's email journey. Form fields must be named: first_name, last_name, email, phone, zip, state.</p>
          </div>
          <Button data-testid="copy-loan-snippet-button" variant="outline" size="sm" onClick={copyLoanSnippet}><Copy className="h-3.5 w-3.5 mr-1.5" /> Copy</Button>
        </div>
        <pre className="bg-slate-900 rounded-lg p-4 text-[11px] text-sky-300 overflow-x-auto scrollbar-thin"><code data-testid="loan-form-snippet">{loanSnippet}</code></pre>
      </Card>
    </div>
  );
}
